
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { EyeIcon, EyeOffIcon, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

interface LoginFormProps {
  onSuccessfulAuth: (isAdmin: boolean) => void;
}

// Enhanced validation schema
const loginSchema = z.object({
  email: z.string()
    .email()
    .max(255)
    .transform(val => val.trim().toLowerCase()),
  password: z.string()
    .min(6)
    .max(128),
});

const LoginForm: React.FC<LoginFormProps> = ({ onSuccessfulAuth }) => {
  const { t } = useLanguage();
  const { signInWithDartsBeat } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [lockoutEndTime, setLockoutEndTime] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Lockout timer effect
  React.useEffect(() => {
    let interval: NodeJS.Timeout;

    if (lockoutEndTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const remaining = Math.max(0, lockoutEndTime - now);

        if (remaining === 0) {
          // Reset lockout
          setLockoutEndTime(null);
          setAttemptCount(0);
          setTimeRemaining(0);
          toast({
            title: t('auth.attemptsReset'),
            description: t('auth.canLoginAgain'),
          });
        } else {
          setTimeRemaining(Math.ceil(remaining / 1000));
        }
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [lockoutEndTime, toast]);

  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    // Check if in lockout period
    if (lockoutEndTime && Date.now() < lockoutEndTime) {
      const minutes = Math.ceil(timeRemaining / 60);
      const seconds = timeRemaining % 60;
      const timeDisplay = minutes > 0
        ? `${minutes}m ${seconds}s`
        : `${seconds}s`;

      toast({
        title: t('auth.tooManyAttempts'),
        description: `${t('auth.waitBeforeRetry')} ${timeDisplay}.`,
        variant: "destructive"
      });
      return;
    }

    // Check if max attempts reached (trigger lockout)
    if (attemptCount >= 5) {
      const lockoutDuration = 10 * 60 * 1000; // 10 minutes
      const endTime = Date.now() + lockoutDuration;
      setLockoutEndTime(endTime);
      setTimeRemaining(lockoutDuration / 1000);

      toast({
        title: t('auth.accountTemporarilyBlocked'),
        description: t('auth.tooManyLoginAttempts'),
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setAttemptCount(prev => prev + 1);

    try {
      // Use DartsBeat-first authentication
      const result = await signInWithDartsBeat(values.email, values.password);

      if (!result.success) {
        // Handle specific error codes
        if (result.errorCode === 'INVALID_DARTSBEAT_CREDENTIALS') {
          toast({
            title: t('auth.incorrectData'),
            description: result.error || t('auth.emailOrPasswordIncorrect'),
            variant: 'destructive',
          });
        } else if (result.errorCode === 'RATE_LIMIT_EXCEEDED') {
          toast({
            title: t('auth.tooManyAttempts'),
            description: result.error || t('auth.waitBeforeRetry'),
            variant: 'destructive',
          });
        } else if (result.errorCode === 'DARTSBEAT_UNAVAILABLE') {
          toast({
            title: t('auth.temporaryError'),
            description: result.error || 'DartsBeat service unavailable',
            variant: 'destructive',
          });
        } else if (result.error?.includes('suspensa') || result.error?.includes('suspended')) {
          toast({
            title: t('auth.accountSuspended'),
            description: result.error || t('auth.contactSupport'),
            variant: 'destructive',
          });
        } else {
          // Generic error
          toast({
            title: t('auth.incorrectData'),
            description: result.error || t('auth.emailOrPasswordIncorrect'),
            variant: 'destructive',
          });
        }
        return;
      }

      // Reset attempt count on successful login
      setAttemptCount(0);

      toast({
        title: t('auth.success'),
        description: t('auth.loginSuccessful'),
      });

      // Secure navigation based on user role
      if (result.isAdmin) {
        navigate('/dashboard');
      } else {
        navigate('/my-account');
      }

      onSuccessfulAuth(result.isAdmin || false);
    } catch (error: any) {
      console.error('Login error:', error);

      // More user-friendly error messages
      const errorMessage = error?.message || '';
      if (errorMessage.toLowerCase().includes('network') || errorMessage.toLowerCase().includes('fetch')) {
        toast({
          title: t('auth.connectionProblem'),
          description: t('auth.checkConnection'),
          variant: "destructive"
        });
      } else {
        toast({
          title: t('auth.temporaryError'),
          description: t('auth.unexpectedError'),
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white">{t('auth.email')}</FormLabel>
              <FormControl>
                <Input 
                  placeholder={t('auth.yourEmail')} 
                  {...field} 
                  className="bg-gray-800 text-white border-gray-600 w-full" 
                  type="email"
                  autoComplete="email"
                  maxLength={255}
                  disabled={isLoading}
                />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white">{t('auth.password')}</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('auth.yourPassword')}
                    {...field} 
                    className="bg-gray-800 text-white border-gray-600 pr-10 w-full" 
                    autoComplete="current-password"
                    maxLength={128}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-white"
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                  </button>
                </div>
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />
        {attemptCount > 2 && attemptCount < 5 && (
          <div className="text-sm text-yellow-400">
            {t('auth.attemptsRemaining')}: {5 - attemptCount}
          </div>
        )}

        {lockoutEndTime && timeRemaining > 0 && (
          <div className="text-sm text-red-400 text-center">
            <div className="font-medium">{t('auth.accountBlocked')}</div>
            <div>
              {t('auth.tryAgainIn')} {Math.floor(timeRemaining / 60)}:
              {String(timeRemaining % 60).padStart(2, '0')}
            </div>
          </div>
        )}
        <Button
          type="submit"
          className="w-full bg-brand-red hover:bg-brand-red/90"
          disabled={isLoading || attemptCount >= 5 || (lockoutEndTime && Date.now() < lockoutEndTime)}
        >
          {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {isLoading ? t('auth.loggingIn') : t('auth.enter')}
        </Button>

        {/* DartsBeat info text */}
        <div className="text-center text-sm text-gray-400">
          <p>{t('auth.dartsbeatLoginInfo') || 'Use as suas credenciais DartsBeat para entrar.'}</p>
        </div>
      </form>
    </Form>
  );
};

export default LoginForm;
